import { ApiAvailables, ApiScoreCard, ApiScoreCardv2, ICertification, ISimulation } from '@/types';
import { DEFAULT_CERTIFICATION_STATUS, DEFAULT_ISSUING_TEAM } from '@/constants';
import { mapApiScorecardsToFrontend, mapSimulationWithHistory } from '@/utils';
import { resolveCertificationPass } from '@/domains/scoring';

// ================================
// mapApiAvailablesToFrontend
// ================================

/**
 * Maps API available certifications to frontend ICertification format.
 */
interface IMapApiAvailablesToFrontendParams {
  /** Array of API available certifications */
  apiAvailables: ApiAvailables[];
  /** Array of API scorecards */
  apiScorecards: (ApiScoreCard | ApiScoreCardv2)[];
}

/**
 * Maps API available certifications to frontend ICertification format.
 *
 * ## What this mapper does
 * For every certification-like "available" payload from the backend, this
 * utility prepares a stable `ICertification` object that the UI can render
 * without additional defensive checks.
 *
 * Main responsibilities:
 * 1. Hydrate simulation `history` by delegating to `mapSimulationWithHistory`.
 * 2. Compute `certPass` by resolving certification scorecards and feeding the
 *    result to `getScorePass`.
 * 3. Apply safe frontend defaults for optional/missing API fields.
 * 4. Return results sorted by ascending `id` for deterministic UI ordering.
 *
 * @param apiAvailables - Array of ApiAvailables from the API
 * @param apiScorecards - Array of ApiScoreCard or ApiScoreCardv2 from the API
 *
 * @returns Array of ICertification formatted for the frontend
 */
export function mapApiAvailablesToFrontend({
  apiAvailables,
  apiScorecards,
}: IMapApiAvailablesToFrontendParams): ICertification[] {
  // ========================================
  // Local Helper: mapHistory
  // ========================================
  // Local helper keeps the mapping call concise and ensures all simulation
  // payloads (practice/certification/regular simulations) receive identical
  // history-enrichment logic.
  const mapHistory = (simulation: ISimulation) => {
    return mapSimulationWithHistory({ simulation, scorecards: apiScorecards });
  };

  // ========================================
  // Main mapping logic
  // ========================================
  return (
    apiAvailables
      .map((available) => {
        // ========================================
        // Map simulations with history enrichment
        // ========================================
        // Defensive: treat null/undefined/empty simulations as an empty array and
        // avoid invoking the history mapper when there is no data to transform.
        let mappedSimulations: ISimulation[] = [];
        if (Array.isArray(available.simulations) && available.simulations.length > 0) {
          mappedSimulations = available.simulations.map(mapHistory);
        }

        // ========================================
        // Resolve certification pass/fail status
        // ========================================
        // Resolve pass/fail for the certification simulation.
        // The score object comes from mapped scorecards filtered to the
        // certification simulation type for the current available.
        // `?? false` preserves the existing behavior where indeterminate pass
        // values are treated as `false` in this mapper.
        const certPass = resolveCertificationPass({
          available,
          apiScorecards,
          dependencies: {
            mapApiScorecardsToFrontend,
          },
        });

        // ========================================
        // Map certification practice with history enrichment
        // ========================================
        // Defensive: only map when a certification practice simulation exists;
        // otherwise leave as undefined to clearly indicate absence of this data.
        const certificationPractice = available.certificationPractice
          ? mapHistory(available.certificationPractice)
          : undefined;

        // ========================================
        // Map certification simulation with history enrichment
        // ========================================
        // Defensive: only map when a certification simulation exists;
        // otherwise leave as undefined to clearly indicate absence of this data.
        const certificationSimulation = available.certificationSimulation
          ? mapHistory(available.certificationSimulation)
          : undefined;

        // ========================================
        // Build the frontend certification object
        // ========================================
        // Build the frontend-facing certification object with stable fallbacks.
        // Empty strings/arrays/default statuses are intentional to simplify UI
        // rendering and avoid repeated null checks in components.
        return {
          category: available.category || '',
          certificationPractice,
          certificationSimulation,
          certPass,
          description: available.description || '',
          dueDate: available.dueDate || '',
          id: available.id,
          issuingTeam: available.issuingTeam || DEFAULT_ISSUING_TEAM,
          personas: available.personas || [],
          progress: available.progress || 0,
          simulations: mappedSimulations,
          status: available.status || DEFAULT_CERTIFICATION_STATUS,
          subtitle: available.subtitle || '',
          title: available.title || '',
          titleShort: available.titleShort || '',
          uuid: available.uuid || '',
        };
      })
      // ========================================
      // Stable sort by ID
      // ========================================
      // Stable sort by numeric ID so rendering order is deterministic regardless
      // of API payload order.
      .sort((a, b) => a.id - b.id)
  );
}
